import { theme, Input, Select, Popover } from 'antd';
import { useState, useMemo, SetStateAction } from 'react';
import {
  Assignment,
  DragItem,
  Column
} from 'store/ducks/InternalControls/types';
import { BprItem } from './BprItem';
import { DraggableItem } from './DraggableItem';
import * as Styled from './InternalControlsDND.styled';

export const ColumnView: React.FC<{
  col: Column;
  assignments: Assignment[];
  onDropRisk: (bprId: number, dragged: DragItem) => void;
  onDropControl: (
    bprId: number,
    riskInstanceId: string,
    dragged: DragItem
  ) => void;
  onDeleteAssignment: (assignmentId: string) => void;
}> = ({ col, assignments, onDropRisk, onDropControl, onDeleteAssignment }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProcessType, setSelectedProcessType] = useState<string | null>(
    null
  );
  const [selectedControlOwner, setSelectedControlOwner] = useState<
    string | null
  >(null);
  const { useToken } = theme;
  const { token } = useToken();

  const categories = useMemo(() => {
    if (col.id !== '2') return [];
    const catList = col.items.map((item: any) => item.category).filter(Boolean);
    return Array.from(new Set(catList)).sort((a, b) => a.localeCompare(b));
  }, [col.items, col.id]);

  const processTypes = useMemo(() => {
    if (col.id !== '1') return [];
    const types = col.items
      .map((item: any) => item.process_type?.name)
      .filter(Boolean);
    return Array.from(new Set(types)).sort((a, b) => a.localeCompare(b));
  }, [col.items, col.id]);

  const controlOwners = useMemo(() => {
    if (col.id !== '3') return [];
    const owners = col.items
      .map((item: any) => item.owner_maintainer)
      .filter(Boolean);
    return Array.from(new Set(owners)).sort((a, b) => a.localeCompare(b));
  }, [col.items, col.id]);

  const filteredItems = useMemo(
    () =>
      col.items.filter((item: any) => {
        const matchesSearch = item.text
          .toLowerCase()
          .includes(searchText.toLowerCase());

        let matchesFilter = true;

        if (col.id === '2' && selectedCategory) {
          matchesFilter = item.category === selectedCategory;
        } else if (col.id === '1' && selectedProcessType) {
          matchesFilter = item.process_type?.name === selectedProcessType;
        } else if (col.id === '3' && selectedControlOwner) {
          matchesFilter = item.owner_maintainer === selectedControlOwner;
        }

        return matchesSearch && matchesFilter;
      }),
    [
      col.items,
      searchText,
      selectedCategory,
      selectedProcessType,
      selectedControlOwner,
      col.id
    ]
  );
  return (
    <Styled.ColumnBox token={token}>
      <Styled.ColumnTitle>{col.title}</Styled.ColumnTitle>

      <Input
        placeholder={`Search ${col.title}`}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />

      {col.id === '2' && categories.length > 0 && (
        <Styled.CustomSelectDropdown
          placeholder='Filter by Category'
          value={selectedCategory}
          onChange={(val: SetStateAction<string | null>) =>
            setSelectedCategory(val)
          }
          allowClear
          options={categories.map((cat) => ({
            label: cat,
            value: cat
          }))}
        />
      )}

      {col.id === '1' && (
        <Select
          placeholder='Filter by Process Type'
          value={selectedProcessType}
          onChange={(val) => setSelectedProcessType(val)}
          allowClear
        >
          {processTypes.map((type) => (
            <Select.Option key={type} value={type}>
              {type}
            </Select.Option>
          ))}
        </Select>
      )}

      {col.id === '3' && (
        <Select
          placeholder='Filter by Owner'
          value={selectedControlOwner}
          onChange={(val) => setSelectedControlOwner(val)}
          allowClear
        >
          {controlOwners.map((owner) => (
            <Select.Option key={owner} value={owner}>
              {owner}
            </Select.Option>
          ))}
        </Select>
      )}

      {filteredItems.map((item) => {
        if (item.type === 'BPR') {
          const criticalityName = item.process_criticality?.name;
          return (
            <Styled.ProcessCriticalityLevel
              key={item.id}
              severity={criticalityName}
            >
              {criticalityName && (
                <Styled.ProcessCriticalityBadge criticality={criticalityName}>
                  {criticalityName}
                </Styled.ProcessCriticalityBadge>
              )}
              <BprItem
                bpr={item}
                assignments={assignments}
                onDropRisk={onDropRisk}
                onDropControl={onDropControl}
                onDeleteAssignment={onDeleteAssignment}
              />
            </Styled.ProcessCriticalityLevel>
          );
        }

        const mapToDragItemType = (itemType: string, columnId: string) => {
          if (itemType === 'BPR') return 'BPR';
          if (columnId === '3') return 'CONTROL';
          if (columnId === '2') return 'RISK';
          return 'CONTROL';
        };

        const dragItem: DragItem = {
          id: item.id,
          text:
            col.id === '2' && item.instances?.[0]?.severity?.name
              ? `${item.text}`
              : item.text,
          description: item.description,
          itemType: mapToDragItemType(item.type, col.id),
          columnId: col.id,
          disabled: item.notUsed,
          severity: item.instances?.[0]?.severity ?? undefined
        };

        const severityName = item.instances?.[0]?.severity?.name;
        return (
          <Styled.RiskSeverityLevel severity={severityName} key={item.id}>
            {severityName && (
              <Styled.SeverityBadge severity={severityName}>
                {severityName}
              </Styled.SeverityBadge>
            )}
            <Popover
              content={
                <div>{item.description || 'No description available'}</div>
              }
            >
              <div>
                <DraggableItem item={dragItem} />
              </div>
            </Popover>
          </Styled.RiskSeverityLevel>
        );
      })}
    </Styled.ColumnBox>
  );
};
